import { TreeNode } from '../index';
import { NodeType, NodeDataTypeMap } from '../base';
import { NodeAction, AuthorizationFunction, IActionHandlerDependencies } from './types';
import { actionHandlers } from './handlers';
import { getPrivilegeForNode, getPrivilegeForEntity } from './getPrivilegeForEntity';
import { IBackend } from '@/api/BackendApi';
import {
  ActivityCategoryDto,
  PrivilegeEnum,
  SampleActivityDto,
  SampleDto,
  StatusDto,
  ActivityTypeDto,
} from '@/api/ApiClient';

import { isExperimentalProject } from '@/lib/types/projectClassification';
import { log } from '@/lib/utils/logger';
import { useTreeStore } from '@/stores/treeStore';
import { isCanonicalDataRouteTree } from '@/lib/tree/dataRouteTreeView';
import {
  canStartNewCharacterizationActivity,
  canStartNewProcessingActivity,
  sampleAllowsTreeMutations,
} from '@/lib/utils/sampleActivityLifecycleEligibility';
import { isSnapshotBuildStaleForRestart } from '@/lib/utils/snapshotRestartEligibility';
import { SHOW_RECREATE_SNAPSHOT_FROM_LIVE_TREE_ACTION } from '@/lib/tree/snapshotRecreateFromLiveFeature';
import { canTransferSampleInTree } from '@/lib/utils/sampleTransferPreflight';
import { findAncestorPendingSnapshotDataPackage } from '@/lib/tree/findAncestorDataPackageContext';

export { getPrivilegeForNode, getPrivilegeForEntity };

/** Owning sample for a sample-activity node (nested `sample` on DTO, else walk parents). */
function getOwningSampleForSampleActivityNode(
  node: TreeNode<'sampleActivity'>,
  activityData: SampleActivityDto | null,
  getNodeData?: (nodeId: string) => TreeNode | null
): SampleDto | null {
  if (activityData?.sample?.id != null) {
    return activityData.sample ?? null;
  }
  if (!getNodeData) {
    return null;
  }
  let parentId: string | undefined = node.parentId;
  for (let depth = 0; depth < 16 && parentId; depth++) {
    const parent = getNodeData(parentId);
    if (!parent) {
      return null;
    }
    if (
      parent.type === 'sample' ||
      parent.type === 'sampleAggregate' ||
      parent.type === 'sampleSnapshot' ||
      parent.type === 'sampleInPackage'
    ) {
      return useTreeStore.getState().getSampleData(parent);
    }
    parentId = parent.parentId;
  }
  return null;
}

/**
 * Type for the node actions registry with proper typing for each node type
 */
type NodeActionsRegistry = {
  project: NodeAction<'project'>[];
  sample: NodeAction<'sample'>[];
  sampleAggregate: NodeAction<'sampleAggregate'>[];
  sampleInPackage: NodeAction<'sampleInPackage'>[];
  sampleSnapshot: NodeAction<'sampleSnapshot'>[];
  sampleActivity: NodeAction<'sampleActivity'>[];
  sampleActivityAggregate: NodeAction<'sampleActivityAggregate'>[];
  sampleActivityInPackage: NodeAction<'sampleActivityInPackage'>[];
  sampleActivitySnapshot: NodeAction<'sampleActivitySnapshot'>[];
  projectActivity: NodeAction<'projectActivity'>[];
  theoryActivity: NodeAction<'theoryActivity'>[];
  theoryActivityInPackage: NodeAction<'theoryActivityInPackage'>[];
  publication: NodeAction<'publication'>[];
  publicationInData: NodeAction<'publicationInData'>[];
  communication: NodeAction<'communication'>[];
  dataPackage: NodeAction<'dataPackage'>[];
  shipping: NodeAction<'shipping'>[];
  oldSamples: NodeAction<'oldSamples'>[];
  newSamples: NodeAction<'newSamples'>[];
  communications: NodeAction<'communications'>[];
  shippings: NodeAction<'shippings'>[];
  simulations: NodeAction<'simulations'>[];
  simulationDataset: NodeAction<'simulationDataset'>[];
  projectInData: NodeAction<'projectInData'>[];
  sampleYearGroup: NodeAction<'sampleYearGroup'>[];
  sampleQuarterGroup: NodeAction<'sampleQuarterGroup'>[];
};

/**
 * Centralized action registry
 */
export const nodeActions: NodeActionsRegistry = {
  project: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'download',
      label: 'Download',
      icon: 'DownloadRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['download'],
    },
    {
      id: 'share',
      label: 'Open Folder',
      icon: 'FolderOpenRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => false,
      handler: actionHandlers['share'],
    },
    {
      id: 'add-proposal',
      label: 'Add Proposal',
      icon: 'AssignmentRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      // /data tree: project root is browse-only for adding project-side content.
      isVisible: (_node, isAuthorized) => !isCanonicalDataRouteTree() && isAuthorized(PrivilegeEnum.Proposal, 'create'),
      handler: actionHandlers['add-proposal'],
    },
    {
      id: 'add-decision',
      label: 'Add Decision',
      icon: 'AssessmentRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) =>
        !isCanonicalDataRouteTree() && isAuthorized(PrivilegeEnum.ProjectDecision, 'create'),
      handler: actionHandlers['add-decision'],
    },
    {
      id: 'add-project-document',
      label: 'Add Project Document',
      icon: 'DescriptionRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) =>
        !isCanonicalDataRouteTree() && isAuthorized(PrivilegeEnum.ProjectDocument, 'create'),
      handler: actionHandlers['add-project-document'],
    },
    {
      id: 'add-report',
      label: 'Add Report',
      icon: 'ArticleRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => !isCanonicalDataRouteTree() && isAuthorized(PrivilegeEnum.Report, 'create'),
      handler: actionHandlers['add-report'],
    },
    {
      id: 'add-publication',
      label: 'Add Publication',
      icon: 'ArticleRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) =>
        !isCanonicalDataRouteTree() && isAuthorized(PrivilegeEnum.Publication, 'create'),
      handler: actionHandlers['add-publication'],
    },
    {
      id: 'add-sample',
      label: 'Add to Inventory',
      icon: 'BlurCircularRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => !isCanonicalDataRouteTree() && isAuthorized(PrivilegeEnum.Sample, 'create'),
      isDisabled: (node, getTypedData) => !isExperimentalProject(getTypedData(node)),
      handler: actionHandlers['add-sample'],
    },
    {
      id: 'add-sample-from-startingMaterials',
      label: 'Add Sample (From StartingMaterials)',
      icon: 'AccountTreeRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => !isCanonicalDataRouteTree() && isAuthorized(PrivilegeEnum.Sample, 'create'),
      isDisabled: (node, getTypedData) => !isExperimentalProject(getTypedData(node)),
      handler: actionHandlers['add-sample-from-startingMaterials'],
    },
    {
      id: 'add-multi-sample-growth',
      label: 'Multi-Sample Growth',
      icon: 'ScienceRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (node, isAuthorized, getTypedData) =>
        !isCanonicalDataRouteTree() &&
        isAuthorized(PrivilegeEnum.Sample, 'create') &&
        isExperimentalProject(getTypedData(node)),
      handler: actionHandlers['add-multi-sample-growth'],
    },
    {
      id: 'add-communication',
      label: 'Add Communication',
      icon: 'ChatRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) =>
        !isCanonicalDataRouteTree() && isAuthorized(PrivilegeEnum.Communication, 'create'),
      handler: actionHandlers['add-communication'],
    },
  ],

  sample: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'download',
      label: 'Download',
      icon: 'DownloadRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['download'],
    },
    {
      id: 'share',
      label: 'Open Folder',
      icon: 'FolderOpenRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => false,
      handler: actionHandlers['share'],
    },
    {
      id: 'add-processing-activity',
      label: 'Add Processing Activity',
      icon: 'ScienceRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized, getTypedData) =>
        sampleAllowsTreeMutations(getTypedData(_node)) && isAuthorized(PrivilegeEnum.ProcessingActivity, 'create'),
      isDisabled: (node, getTypedData) => !canStartNewProcessingActivity(getTypedData(node)),
      handler: actionHandlers['add-processing-activity'],
    },
    {
      id: 'add-char-activity',
      label: 'Add Characterization Activity',
      icon: 'BiotechRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized, getTypedData) =>
        sampleAllowsTreeMutations(getTypedData(_node)) &&
        isAuthorized(PrivilegeEnum.CharacterizationActivity, 'create'),
      isDisabled: (node, getTypedData) => {
        const sample = getTypedData(node);
        return sample?.finalized === true || !canStartNewCharacterizationActivity(sample ?? undefined);
      },
      handler: actionHandlers['add-char-activity'],
    },
    {
      id: 'split',
      label: 'Split Sample',
      icon: 'CallSplit',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized, getTypedData) =>
        sampleAllowsTreeMutations(getTypedData(_node)) && isAuthorized(PrivilegeEnum.SplitActivity, 'create'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.finalized === true,
      handler: actionHandlers['split'],
    },
    {
      id: 'transfer',
      label: 'Transfer Sample to other project(s)',
      icon: 'SwapHoriz',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized, getTypedData) => canTransferSampleInTree(getTypedData(_node), isAuthorized),
      isDisabled: (node, getTypedData) => getTypedData(node)?.finalized === true,
      handler: actionHandlers['transfer'],
    },
    {
      id: 'add-storage',
      label: 'Add Storage Information',
      icon: 'Storage',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized, getTypedData) =>
        sampleAllowsTreeMutations(getTypedData(_node)) && isAuthorized(PrivilegeEnum.StorageActivity, 'create'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.finalized === true,
      handler: actionHandlers['add-storage'],
    },
    {
      id: 'duplicate',
      label: 'Duplicate',
      icon: 'Duplicate',
      showInContextMenu: false, // Hidden - needs implementation
      showInAddMenu: false,
      isVisible: (_node, isAuthorized, getTypedData) =>
        sampleAllowsTreeMutations(getTypedData(_node)) && isAuthorized(PrivilegeEnum.Sample, 'create'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.finalized === true,
      handler: actionHandlers['duplicate'],
    },
    {
      id: 'submit',
      label: 'Submit',
      icon: 'CheckCircle',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized, getTypedData) =>
        sampleAllowsTreeMutations(getTypedData(_node)) && isAuthorized(PrivilegeEnum.Sample, 'submit'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status !== StatusDto.Pending,
      handler: actionHandlers['submit'],
    },
    {
      id: 'reset',
      label: 'Reset',
      icon: 'RestartAlt',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized, getTypedData) =>
        sampleAllowsTreeMutations(getTypedData(_node)) && isAuthorized(PrivilegeEnum.Sample, 'reset'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status !== StatusDto.Submitted,
      handler: actionHandlers['reset'],
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Delete',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized, getTypedData) =>
        sampleAllowsTreeMutations(getTypedData(_node)) && isAuthorized(PrivilegeEnum.Sample, 'delete'),
      isDisabled: (node, getTypedData) =>
        getTypedData(node)?.finalized === true || getTypedData(node)?.status === StatusDto.Submitted,
      handler: actionHandlers['delete'],
    },
    {
      id: 'view-data',
      label: 'View Data',
      icon: 'VisibilityRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => false,
      handler: actionHandlers['view-data'],
    },
  ],

  simulations: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'add-data-package',
      label: 'Add Data Package',
      icon: 'BlurCircularRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.DataPackage, 'create'),
      handler: actionHandlers['add-data-package'],
    },
  ],

  newSamples: [
    {
      id: 'add-sample',
      label: 'Add to Inventory',
      icon: 'BlurCircularRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.Sample, 'create'),
      handler: actionHandlers['add-sample'],
    },
    {
      id: 'add-sample-from-startingMaterials',
      label: 'Add Sample (From StartingMaterials)',
      icon: 'AccountTreeRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.Sample, 'create'),
      handler: actionHandlers['add-sample-from-startingMaterials'],
    },
    {
      id: 'add-multi-sample-growth',
      label: 'Multi-Sample Growth',
      icon: 'ScienceRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.Sample, 'create'),
      handler: actionHandlers['add-multi-sample-growth'],
    },
  ],

  simulationDataset: [
    {
      id: 'add-theory-activity',
      label: 'Add Simulation',
      icon: 'ArchiveRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.TheoryActivity, 'create'),
      handler: actionHandlers['add-theory-activity'],
    },
  ],

  communications: [
    {
      id: 'add-communication',
      label: 'Add Communication',
      icon: 'ChatRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.Communication, 'create'),
      handler: actionHandlers['add-communication'],
    },
  ],

  sampleActivity: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'download',
      label: 'Download',
      icon: 'DownloadRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['download'],
    },
    {
      id: 'share',
      label: 'Open Folder',
      icon: 'FolderOpenRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => false,
      handler: actionHandlers['share'],
    },
    {
      id: 'add-in-situ-char-activity',
      label: 'Add In-Situ Characterization',
      icon: 'BiotechRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (node, isAuthorized, getTypedData, getNodeData) => {
        const activityData = getTypedData(node);
        const owner = getOwningSampleForSampleActivityNode(node, activityData, getNodeData);
        if (owner && !sampleAllowsTreeMutations(owner)) {
          return false;
        }
        return !!(
          (activityData?.type === ActivityTypeDto.Synthesis ||
            activityData?.type === ActivityTypeDto.Preparation ||
            activityData?.type === ActivityTypeDto.PostTreatment) &&
          isAuthorized(PrivilegeEnum.CharacterizationActivity, 'create')
        );
      },
      isDisabled: (node, getTypedData, getNodeData) => {
        const activityData = getTypedData(node);
        const owner = getOwningSampleForSampleActivityNode(node, activityData, getNodeData);
        return owner?.shipped === true;
      },
      handler: actionHandlers['add-in-situ-char-activity'],
    },
    {
      id: 'submit',
      label: 'Submit',
      icon: 'CheckCircle',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => isAuthorized(getPrivilegeForNode(node, getTypedData), 'submit'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status !== StatusDto.Pending,
      handler: actionHandlers['submit'],
    },
    {
      id: 'reset',
      label: 'Reset',
      icon: 'RestartAlt',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => isAuthorized(getPrivilegeForNode(node, getTypedData), 'reset'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status !== StatusDto.Submitted,
      handler: actionHandlers['reset'],
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Delete',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => {
        const activity = getTypedData(node);
        // Hide delete for Inventory activities – they must be deleted via the parent sample.
        if (activity?.category === ActivityCategoryDto.Inventory) {
          return false;
        }
        return isAuthorized(getPrivilegeForNode(node, getTypedData), 'delete');
      },
      isDisabled: (node, getTypedData) => getTypedData(node)?.status === StatusDto.Submitted,
      handler: actionHandlers['delete'],
    },
  ],

  projectActivity: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'download',
      label: 'Download',
      icon: 'DownloadRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['download'],
    },
    {
      id: 'submit',
      label: 'Submit',
      icon: 'CheckCircle',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized, getTypedData) =>
        isAuthorized(getPrivilegeForNode(_node, getTypedData), 'submit'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status !== StatusDto.Pending,
      handler: actionHandlers['submit'],
    },
    {
      id: 'reset',
      label: 'Reset',
      icon: 'RestartAlt',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized, getTypedData) => isAuthorized(getPrivilegeForNode(_node, getTypedData), 'reset'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status !== StatusDto.Submitted,
      handler: actionHandlers['reset'],
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Delete',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized, getTypedData) =>
        isAuthorized(getPrivilegeForNode(_node, getTypedData), 'delete'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status === StatusDto.Submitted,
      handler: actionHandlers['delete'],
    },
  ],

  shippings: [
    {
      id: 'ship',
      label: 'Ship / Transfer Sample(s)',
      icon: 'LocalShippingRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.Shipping, 'create'),
      handler: actionHandlers['ship'],
    },
  ],

  // Missing node types from old ContextMenu
  communication: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Delete',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.Communication, 'delete'),
      handler: actionHandlers['delete'],
    },
  ],

  shipping: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'download',
      label: 'Download',
      icon: 'DownloadRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['download'],
    },
    {
      id: 'share',
      label: 'Open Folder',
      icon: 'FolderOpenRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => false,
      handler: actionHandlers['share'],
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Delete',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.Shipping, 'delete'),
      handler: actionHandlers['delete'],
    },
  ],

  publication: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'share',
      label: 'Open Folder',
      icon: 'FolderOpenRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => false,
      handler: actionHandlers['share'],
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Delete',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.Publication, 'delete'),
      handler: actionHandlers['delete'],
    },
  ],

  dataPackage: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'download',
      label: 'Download',
      icon: 'DownloadRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['download'],
    },
    {
      id: 'share',
      label: 'Open Folder',
      icon: 'FolderOpenRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => false,
      handler: actionHandlers['share'],
    },
    {
      id: 'add-theory-activity-to-data-package',
      label: 'Add Simulation',
      icon: 'MemoryRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.DataPackage, 'update'),
      isDisabled: (node, getTypedData) => {
        const data = getTypedData(node);
        if (!data) return true;
        // Disable if:
        // - Published (any type)
        // - Submitted snapshot
        // - Snapshot creation in progress
        return !!(
          data.status === StatusDto.Published ||
          (data.isSnapshot && data.status === StatusDto.Submitted) ||
          (data.isSnapshot && data.percentSnapshotDone !== undefined && data.percentSnapshotDone < 100)
        );
      },
      handler: actionHandlers['add-theory-activity-to-data-package'],
    },
    {
      id: 'add-sample-to-data-package',
      label: 'Add Samples',
      icon: 'BlurCircularRounded',
      showInContextMenu: true,
      showInAddMenu: true,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.DataPackage, 'update'),
      isDisabled: (node, getTypedData) => {
        const data = getTypedData(node);
        if (!data) return true;
        // Disable if:
        // - Published (any type)
        // - Submitted snapshot
        // - Snapshot creation in progress
        return !!(
          data.status === StatusDto.Published ||
          (data.isSnapshot && data.status === StatusDto.Submitted) ||
          (data.isSnapshot && data.percentSnapshotDone !== undefined && data.percentSnapshotDone < 100)
        );
      },
      handler: actionHandlers['add-sample-to-data-package'],
    },
    {
      id: 'create-snapshot',
      label: 'Create Snapshot',
      icon: 'CameraAltRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => {
        const data = getTypedData(node);
        return !!(isAuthorized(PrivilegeEnum.DataSnapshot, 'create') && !data?.isSnapshot);
      },
      isDisabled: (node, getTypedData) => {
        const data = getTypedData(node);
        return !data || data.status !== StatusDto.Pending;
      },
      handler: actionHandlers['create-snapshot'],
    },
    {
      id: 'restart-snapshot',
      label: 'Resume stalled snapshot',
      icon: 'CameraAltRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => {
        const data = getTypedData(node);
        const incomplete = !!(
          data?.status === StatusDto.Pending &&
          data?.isSnapshot &&
          (data.percentSnapshotDone ?? 0) < 100
        );
        return !!(
          isAuthorized(PrivilegeEnum.DataSnapshot, 'create') &&
          incomplete &&
          isSnapshotBuildStaleForRestart(data?.lastChangedDate)
        );
      },
      isDisabled: (node, getTypedData) => {
        const data = getTypedData(node);
        return !data || data.status !== StatusDto.Pending;
      },
      handler: actionHandlers['restart-snapshot'],
    },
    {
      id: 'submit-snapshot',
      label: 'Submit',
      icon: 'CheckCircle',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => {
        const data = getTypedData(node);
        return !!(isAuthorized(PrivilegeEnum.DataSnapshot, 'submit') && data?.isSnapshot);
      },
      isDisabled: (node, getTypedData) => {
        const data = getTypedData(node);
        if (!data) return true;
        if (data.status !== StatusDto.Pending) return true;
        if (data.percentSnapshotDone !== undefined && data.percentSnapshotDone < 100) return true;
        return false;
      },
      handler: actionHandlers['submit'],
    },
    {
      id: 'publish-data-package',
      label: 'Publish',
      icon: 'PublishRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => {
        const data = getTypedData(node);
        if (!data) return false;
        if (data.isSnapshot) {
          return isAuthorized(PrivilegeEnum.DataSnapshot, 'execute');
        }
        return isAuthorized(PrivilegeEnum.DataPackage, 'execute');
      },
      isDisabled: (node, getTypedData) => {
        const data = getTypedData(node);
        if (!data) return true;
        if (data.isSnapshot) {
          if (data.percentSnapshotDone !== undefined && data.percentSnapshotDone < 100) return true;
          return data.status !== StatusDto.Submitted;
        }
        return data.status !== StatusDto.Pending;
      },
      handler: actionHandlers['publish-data-package'],
    },
    {
      id: 'set-doi',
      label: 'Set DOI',
      icon: 'LinkRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => {
        const data = getTypedData(node);
        // DOI assignment applies only to snapshot rows; status / DOI fill state control disabled below.
        return !!(isAuthorized(PrivilegeEnum.DataPackage, 'update') && data?.isSnapshot);
      },
      isDisabled: (node, getTypedData) => {
        const data = getTypedData(node);
        if (!data) return true;
        if (data.status !== StatusDto.Published) return true;
        const hasDataDoi = !!(data.doi && data.doi.trim());
        const hasPublicationDoi = !!(data.publicationDoi && data.publicationDoi.trim());
        return hasDataDoi && hasPublicationDoi;
      },
      handler: actionHandlers['set-doi'],
    },
    {
      id: 'reset',
      label: 'Reset to Pending',
      icon: 'RestartAlt',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData) => {
        const data = getTypedData(node);
        return !!(isAuthorized(PrivilegeEnum.DataSnapshot, 'reset') && data?.isSnapshot);
      },
      isDisabled: (node, getTypedData) => {
        const data = getTypedData(node);
        return !data || data.status !== StatusDto.Submitted;
      },
      handler: actionHandlers['reset'],
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Delete',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.DataPackage, 'delete'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status === StatusDto.Published,
      handler: actionHandlers['delete'],
    },
  ],

  theoryActivity: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'submit',
      label: 'Submit',
      icon: 'CheckCircle',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.TheoryActivity, 'submit'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status !== StatusDto.Pending,
      handler: actionHandlers['submit'],
    },
    {
      id: 'reset',
      label: 'Reset',
      icon: 'RestartAlt',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.TheoryActivity, 'reset'),
      isDisabled: (node, getTypedData) => getTypedData(node)?.status !== StatusDto.Submitted,
      handler: actionHandlers['reset'],
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: 'Delete',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized) => isAuthorized(PrivilegeEnum.TheoryActivity, 'delete'),
      handler: actionHandlers['delete'],
    },
  ],
  oldSamples: [],

  // Published aggregate (/samples merge) — reload only
  sampleAggregate: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
  ],

  sampleActivityAggregate: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
  ],

  // Read-only node types (at least reload action)
  sampleInPackage: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'remove-from-data-package',
      label: 'Remove from Data Package',
      icon: 'RemoveRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized, _getTypedData) => {
        // Get the data package status from parent
        // For now, just check if user has update privileges
        return isAuthorized(PrivilegeEnum.DataPackage, 'update');
      },
      handler: actionHandlers['remove-from-data-package'],
    },
  ],

  sampleSnapshot: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'remove-from-snapshot',
      label: 'Remove from Snapshot',
      icon: 'RemoveCircleRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData, getNodeData) => {
        if (!isAuthorized(PrivilegeEnum.DataPackage, 'update')) return false;
        return (
          findAncestorPendingSnapshotDataPackage(node, getNodeData ?? (() => null), (n) =>
            getTypedData(n as typeof node)
          ) !== null
        );
      },
      handler: actionHandlers['remove-from-snapshot'],
    },
    {
      id: 'recreate-snapshot-from-live',
      label: 'Recreate from Live Data',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData, getNodeData) => {
        if (!SHOW_RECREATE_SNAPSHOT_FROM_LIVE_TREE_ACTION) return false;
        if (!isAuthorized(PrivilegeEnum.DataSnapshot, 'update')) return false;
        return (
          findAncestorPendingSnapshotDataPackage(node, getNodeData ?? (() => null), (n) =>
            getTypedData(n as typeof node)
          ) !== null
        );
      },
      handler: actionHandlers['recreate-snapshot-from-live'],
    },
  ],

  sampleActivityInPackage: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
  ],

  sampleActivitySnapshot: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'remove-from-snapshot',
      label: 'Remove from Snapshot',
      icon: 'RemoveCircleRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData, getNodeData) => {
        if (!isAuthorized(PrivilegeEnum.DataPackage, 'update')) return false;
        return (
          findAncestorPendingSnapshotDataPackage(node, getNodeData ?? (() => null), (n) =>
            getTypedData(n as typeof node)
          ) !== null
        );
      },
      handler: actionHandlers['remove-from-snapshot'],
    },
    {
      id: 'recreate-snapshot-from-live',
      label: 'Recreate from Live Data',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (node, isAuthorized, getTypedData, getNodeData) => {
        if (!SHOW_RECREATE_SNAPSHOT_FROM_LIVE_TREE_ACTION) return false;
        if (!isAuthorized(PrivilegeEnum.DataSnapshot, 'update')) return false;
        return (
          findAncestorPendingSnapshotDataPackage(node, getNodeData ?? (() => null), (n) =>
            getTypedData(n as typeof node)
          ) !== null
        );
      },
      handler: actionHandlers['recreate-snapshot-from-live'],
    },
  ],

  theoryActivityInPackage: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
    {
      id: 'remove-from-data-package',
      label: 'Remove from Data Package',
      icon: 'RemoveRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: (_node, isAuthorized) => {
        // Note: Snapshot in-progress check is handled by handler validation
        return isAuthorized(PrivilegeEnum.DataPackage, 'update');
      },
      handler: actionHandlers['remove-from-data-package'],
    },
  ],

  projectInData: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
  ],

  publicationInData: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
  ],

  // Group node types (at least reload action)
  sampleYearGroup: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
  ],

  sampleQuarterGroup: [
    {
      id: 'reload',
      label: 'Reload',
      icon: 'RefreshRounded',
      showInContextMenu: true,
      showInAddMenu: false,
      isVisible: () => true,
      handler: actionHandlers['reload'],
    },
  ],
};

/**
 * Helper functions to get actions for different contexts
 */
export const getContextMenuActions = <T extends NodeType>(
  nodeType: T,
  node: TreeNode<T>,
  isAuthorized: AuthorizationFunction,
  getTypedData: (node: TreeNode<T>) => NodeDataTypeMap[T] | null,
  getNodeData?: (nodeId: string) => TreeNode | null
) => {
  const actions = (nodeActions as Record<string, NodeAction<any>[]>)[nodeType] || [];
  return actions.filter(
    (action) => action.showInContextMenu && action.isVisible(node, isAuthorized, getTypedData, getNodeData)
  );
};

export const getAddMenuActions = <T extends NodeType>(
  nodeType: T,
  node: TreeNode<T>,
  isAuthorized: AuthorizationFunction,
  getTypedData: (node: TreeNode<T>) => NodeDataTypeMap[T] | null,
  getNodeData?: (nodeId: string) => TreeNode | null
) => {
  const actions = (nodeActions as Record<string, NodeAction<any>[]>)[nodeType] || [];
  return actions.filter(
    (action) => action.showInAddMenu && action.isVisible(node, isAuthorized, getTypedData, getNodeData)
  );
};

export const getAllAvailableActions = <T extends NodeType>(
  nodeType: T,
  node: TreeNode<T>,
  isAuthorized: AuthorizationFunction,
  getTypedData: (node: TreeNode<T>) => NodeDataTypeMap[T] | null,
  getNodeData?: (nodeId: string) => TreeNode | null
) => {
  const actions = (nodeActions as Record<string, NodeAction<any>[]>)[nodeType] || [];
  return actions.filter((action) => action.isVisible(node, isAuthorized, getTypedData, getNodeData));
};

export const isActionDisabled = <T extends NodeType>(
  action: NodeAction<T>,
  node: TreeNode<T>,
  getTypedData: (node: TreeNode<T>) => NodeDataTypeMap[T] | null,
  getNodeData?: (nodeId: string) => TreeNode | null
): boolean => {
  return action.isDisabled ? action.isDisabled(node, getTypedData, getNodeData) : false;
};

export const executeAction = async (
  actionId: string,
  node: TreeNode,
  backend: IBackend,
  deps: IActionHandlerDependencies
) => {
  const actions = (nodeActions as Record<string, NodeAction<any>[]>)[node.type] || [];
  const action = actions.find((a) => a.id === actionId);

  if (!action) {
    log.warn('[NodeActions] Action not found:', actionId);
    return null;
  }

  try {
    deps.markLoading(action.id, true);
    return await action.handler(node, backend, deps);
  } catch (error) {
    log.error('[NodeActions] Error executing action:', { actionId, error });
    throw error;
  } finally {
    deps.markLoading(action.id, false);
  }
};
